import React from "react";
import { Download } from "lucide-react";

const ADS = [
  { file: "ad-01-hero-square.png",      title: "Hero · Retatrutide",     size: "1:1 Square",      use: "Instagram & Facebook feed" },
  { file: "ad-02-catalog-square.png",   title: "Catalog · 3 Vials",      size: "1:1 Square",      use: "Instagram & Facebook feed" },
  { file: "ad-03-vertical-story.png",   title: "Story · Scan Ship",      size: "9:16 Vertical",   use: "Instagram & TikTok Stories" },
  { file: "ad-04-vertical-cta.png",     title: "Story · Scan QR",        size: "9:16 Vertical",   use: "Instagram & TikTok Stories" },
  { file: "ad-05-landscape-banner.png", title: "Wide · Banner",          size: "16:9 Landscape",  use: "Facebook cover / LinkedIn / YouTube" },
];

const VIDEO = { file: "ad-video-vertical.mp4", title: "Reveal · 4s Vertical Video", size: "9:16 Vertical", use: "Reels / TikTok / Shorts" };

export default function MarketingGallery() {
  const download = (file) => {
    const link = document.createElement("a");
    link.href = `/marketing/${file}`;
    link.download = file;
    link.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="marketing-gallery">
      <div className="label-mono text-cyan-400 mb-3">/ MARKETING ASSETS</div>
      <h1 className="text-4xl md:text-5xl font-medium tracking-tight">Ads & Promo Kit</h1>
      <p className="mt-3 text-slate-400 max-w-2xl">
        Ready-to-post brand creatives generated in the Novagenix Labs dark-cyber aesthetic.
        Click any tile to preview at full size, or hit download to grab the original PNG / MP4.
      </p>
      <p className="mt-3 text-xs text-amber-200/80 leading-relaxed max-w-2xl">
        ⚠ Research-use-only branding — these assets carry no medical / health claims. Pair with
        the standard "Not for human consumption" disclaimer on every post.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ads-grid">
        {ADS.map((ad) => (
          <a
            key={ad.file}
            href={`/marketing/${ad.file}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`ad-tile-${ad.file}`}
            className="block group border border-white/10 hover:border-cyan-500/40 transition bg-slate-950/40"
          >
            <div className="aspect-square overflow-hidden bg-slate-900 relative">
              <img
                src={`/marketing/${ad.file}`}
                alt={ad.title}
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
                loading="lazy"
              />
              <div className="absolute top-3 left-3 label-mono text-cyan-400 bg-slate-950/80 px-2 py-1 border border-cyan-500/20">
                {ad.size}
              </div>
            </div>
            <div className="p-4">
              <div className="font-medium">{ad.title}</div>
              <div className="text-xs text-slate-400 mt-1">{ad.use}</div>
              <button
                onClick={(e) => { e.preventDefault(); download(ad.file); }}
                className="mt-3 inline-flex items-center gap-2 label-mono text-cyan-400 hover:text-cyan-300"
                data-testid={`download-${ad.file}`}
              >
                <Download size={12} /> Download PNG
              </button>
            </div>
          </a>
        ))}
      </div>

      {/* Video card */}
      <div className="mt-12">
        <div className="label-mono text-cyan-400 mb-4">/ VIDEO</div>
        <div className="border border-white/10 bg-slate-950/40 max-w-md" data-testid="ad-video-card">
          <div className="aspect-[9/16] bg-slate-900 relative overflow-hidden">
            <video
              src={`/marketing/${VIDEO.file}`}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
              data-testid="ad-video-player"
              onError={(e) => { e.target.style.display = "none"; e.target.parentElement.querySelector(".video-missing").classList.remove("hidden"); }}
            />
            <div className="video-missing hidden absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-slate-500">
              <div className="label-mono text-amber-400 mb-2">VIDEO NOT YET GENERATED</div>
              <p className="text-xs leading-relaxed">
                Top up your Universal Key balance, then run:<br />
                <code className="text-cyan-400">python3 /app/scripts/generate_ads.py video</code>
              </p>
            </div>
            <div className="absolute top-3 left-3 label-mono text-cyan-400 bg-slate-950/80 px-2 py-1 border border-cyan-500/20">
              {VIDEO.size}
            </div>
          </div>
          <div className="p-4">
            <div className="font-medium">{VIDEO.title}</div>
            <div className="text-xs text-slate-400 mt-1">{VIDEO.use}</div>
            <button
              onClick={() => download(VIDEO.file)}
              className="mt-3 inline-flex items-center gap-2 label-mono text-cyan-400 hover:text-cyan-300"
              data-testid="download-video"
            >
              <Download size={12} /> Download MP4
            </button>
          </div>
        </div>
      </div>

      <div className="mt-12 border border-white/5 p-5 max-w-2xl text-xs text-slate-400 leading-relaxed">
        <span className="label-mono text-cyan-400 block mb-2">Regenerate / add more</span>
        Edit prompts in <code className="text-cyan-400">/app/scripts/generate_ads.py</code> and
        run the script again. Each image is ~$0.04-0.08, the video runs ~$0.20-0.40 — keep
        an eye on your Universal Key balance in Profile → Universal Key.
      </div>
    </div>
  );
}
